import { Reducer, useContext, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import managerDashboardApi from "../../../api/dashboard/managerDashboardApi";
import dualPrepJourneyHelper from "../../../helpers/dualPrepJourneyHelper";
import { userDetailsHelper } from "../../../helpers";
import UserContext from "../../../state/UserContext";
import ManagerDashboardUpdateDto from "../../../types/dtos/dashboards/ManagerDashboardUpdateDto";
import { BaseUserDetailsDto } from "../../../types/dtos/generic";
import SkipManagerPlanningPopUp from "../SkipManagerPlanningPopUp";

interface SkipPlanningPopupState {
  isOpen: boolean;
  subjectUserName: string | null;
  subjectUserId: number | null;
  journeyRef: string | null;
  clientFormTitle: string | null;
  answerSetUniqueId: string | null;
  skipPlanningConfirmationTicked: boolean;
  isLoading: boolean;
}

const getDefaultSkipPlanningPopupState = (): SkipPlanningPopupState => ({
  isOpen: false,
  subjectUserName: null,
  subjectUserId: null,
  journeyRef: null,
  clientFormTitle: null,
  answerSetUniqueId: null,
  skipPlanningConfirmationTicked: false,
  isLoading: false,
});

/** Shared skip-planning and dual-prep navigation used by UpdatesWidget and the homepage team sidebar. */
export function useManagerTodoActions(
  users: BaseUserDetailsDto[],
  onTodosChanged: () => void
) {
  const navigate = useNavigate();
  const auth = useAuth();
  const userContext = useContext(UserContext);
  const dashboardApi = new managerDashboardApi(auth.user?.access_token);

  const [skipPlanningPopUpState, setSkipPlanningPopUpState] = useReducer<
    Reducer<SkipPlanningPopupState, Partial<SkipPlanningPopupState>>
  >(
    (state, newState) => ({ ...state, ...newState }),
    getDefaultSkipPlanningPopupState()
  );
  const [showSuccessNotification, setShowSuccessNotification] =
    useState<boolean>(false);
  const [successNotificationMessage, setSuccessNotificationMessage] =
    useState<string>("");
  const [showErrorNotification, setShowErrorNotification] =
    useState<boolean>(false);
  const [errorNotificationMessage, setErrorNotificationMessage] =
    useState<string>("");

  const triggerSkipConfirmationModalChange = () => {
    setSkipPlanningPopUpState({
      isOpen: !skipPlanningPopUpState.isOpen,
      skipPlanningConfirmationTicked: false,
    });
  };

  const handleSkipConfirmationModalClick = (
    update: ManagerDashboardUpdateDto
  ) => {
    const subjectUserName = userDetailsHelper.getDisplayName(
      update.userId,
      users
    );
    triggerSkipConfirmationModalChange();
    setSkipPlanningPopUpState({
      isOpen: true,
      subjectUserName,
      subjectUserId: update.userId,
      journeyRef: update.journeyReference,
      answerSetUniqueId: update.answerSetUniqueId,
      clientFormTitle: update.clientFormTitle,
    });
  };

  const handleLoadDualPrepCollabDoc = (
    employeeId: number,
    journeyRef: string
  ) => {
    const dualPrepHelper = new dualPrepJourneyHelper(dashboardApi, userContext);
    dualPrepHelper.openNewCollabDocByJourneyRef(
      employeeId,
      journeyRef,
      (destinationUrl: string) => {
        navigate(destinationUrl);
      }
    );
  };

  const onConfirmSkipPlanning = () => {
    setSkipPlanningPopUpState({ isLoading: true });
    if (skipPlanningPopUpState.skipPlanningConfirmationTicked) {
      dashboardApi.skipManagerPlanning(
        skipPlanningPopUpState.subjectUserId!,
        skipPlanningPopUpState.journeyRef!,
        skipPlanningPopUpState.answerSetUniqueId!,
        () => {
          setShowSuccessNotification(true);
          setSuccessNotificationMessage(
            `${skipPlanningPopUpState.clientFormTitle} planning for ${skipPlanningPopUpState.subjectUserName} has been skipped`
          );
          setShowErrorNotification(false);
          setErrorNotificationMessage("");
          setSkipPlanningPopUpState(getDefaultSkipPlanningPopupState());
          onTodosChanged();
        },
        (error: unknown) => {
          setShowErrorNotification(true);
          setErrorNotificationMessage("Error");
          console.error(error);
          setSkipPlanningPopUpState(getDefaultSkipPlanningPopupState());
        }
      );
    }
  };

  const skipPlanningPopUp = (
    <SkipManagerPlanningPopUp
      isOpen={skipPlanningPopUpState.isOpen}
      displayName={skipPlanningPopUpState.subjectUserName!}
      clientFormTitle={skipPlanningPopUpState.clientFormTitle!}
      onOpenChange={triggerSkipConfirmationModalChange}
      onConfirmButtonClick={onConfirmSkipPlanning}
      onCancelButtonClick={() =>
        setSkipPlanningPopUpState(getDefaultSkipPlanningPopupState())
      }
      skipPlanningConfirmationTicked={
        skipPlanningPopUpState.skipPlanningConfirmationTicked
      }
      handleSkipPlanningConfirmationTicked={(isTicked: boolean) =>
        setSkipPlanningPopUpState({ skipPlanningConfirmationTicked: isTicked })
      }
      isLoading={skipPlanningPopUpState.isLoading}
    />
  );

  return {
    handleSkipConfirmationModalClick,
    handleLoadDualPrepCollabDoc,
    skipPlanningPopUp,
    showSuccessNotification,
    successNotificationMessage,
    showErrorNotification,
    errorNotificationMessage,
  };
}

export default useManagerTodoActions;
